export type PixelCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
} | null;

export type FileValidationResult = {
  accepted: File[];
  wrongType: File[];
  tooLarge: File[];
};

export const hasAllowedFileType = (
  file: Pick<File, "name">,
  allowedFileTypes: string[],
): boolean => {
  const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
  return !!extension && allowedFileTypes.includes(extension);
};

/**
 * Splits files by why they were rejected, so the caller can report one error
 * per reason listing every offending file, rather than one error per file.
 *
 * A file with a disallowed type is only reported as such, even when it is also
 * over the size limit — one reason per file is enough to act on.
 */
export const partitionFilesByValidity = <T extends Pick<File, "name" | "size">>(
  files: T[],
  allowedFileTypes: string[],
  maxFileSize: number,
): { accepted: T[]; wrongType: T[]; tooLarge: T[] } => {
  const accepted: T[] = [];
  const wrongType: T[] = [];
  const tooLarge: T[] = [];

  for (const file of files) {
    if (!hasAllowedFileType(file, allowedFileTypes)) wrongType.push(file);
    else if (file.size > maxFileSize) tooLarge.push(file);
    else accepted.push(file);
  }

  return { accepted, wrongType, tooLarge };
};

export const readFile = (file: File): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
};

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

const getCroppedImg = async (
  contentType: string,
  imageSrc: string,
  pixelCrop: PixelCrop = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  },
  rotation = 0,
  flip = { horizontal: false, vertical: false },
): Promise<{ file: Blob; url: string } | null> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx || !pixelCrop) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  // croppedAreaPixels values are bounding box relative. The crop may extend
  // past the image (restrictPosition is off), so those areas are transparent.
  const output = document.createElement("canvas");
  const outCtx = output.getContext("2d");
  if (!outCtx) return null;
  output.width = pixelCrop.width;
  output.height = pixelCrop.height;

  // Formats without alpha turn transparent pixels into garbage, so fill white
  if (!/png|webp|svg/.test(contentType)) {
    outCtx.fillStyle = "#fff";
    outCtx.fillRect(0, 0, output.width, output.height);
  }
  outCtx.drawImage(canvas, -pixelCrop.x, -pixelCrop.y);

  return new Promise((resolve) => {
    output.toBlob((file) => {
      if (!file) return resolve(null);
      resolve({ file, url: URL.createObjectURL(file) });
    }, contentType);
  });
};

const getImgDimensions = (
  imageSrc: string,
): Promise<{ width: number; height: number }> =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const height = image.height;
      const width = image.width;
      resolve({ width, height });
    };
    image.src = imageSrc;
  });

export async function getAspectRatio(imgSrc: string) {
  const { width, height } = await getImgDimensions(imgSrc);

  let aspectRatio;

  if (width > height) {
    aspectRatio = width / height;
  } else {
    aspectRatio = height / width;
  }

  return aspectRatio;
}

export function calculateMaxDimensions(
  aspectRatio: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  let width = maxWidth;
  let height = maxWidth / aspectRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = maxHeight * aspectRatio;
  }

  return { width, height };
}

export const toBase64 = (image: Blob): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(image);
  });

export default getCroppedImg;
